import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { handleError, handleResponse } from 'src/functions/http-handler';
import { environment } from 'src/environments/environment';
import { FileSaverService } from 'ngx-filesaver';
import { tap, catchError, map } from "rxjs/operators";
import { IResponse } from 'src/models/response.model';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {
  private _apiUrl: string;

  constructor(
    @Inject(HttpClient) protected http: HttpClient,
    @Inject('BASE_URL') baseUrl: string,
    private fileSaver: FileSaverService,
    private logger: NGXLogger) {
    this._apiUrl = `${baseUrl}api/Attachment`;
  }

  get(id: string) {
    return this.http.get(this._apiUrl, { params: { Ids: [id] } })
      .pipe(handleResponse<IAttachment>(this.logger, 'get attachemnt info'));
  }

  getMultiple(ids: Array<string>) {
    return this.http.get(this._apiUrl, { params: { Ids: ids } })
      .pipe(
        tap((res) => this.logger.log('get attachment multiple', res)),
        catchError(handleError),
        map((data: IResponse) => {
          // 이전 버전 호환성을 위해서 attachment end-point에서는
          // 단일 요청 반환에 대해서는 Array 형태로 제공하지 않는다.
          // component에서는 이를 매번 확인하기 번거롭기 때문에, service에서 Array형태로 통일시켜 반환해줌.
          if (Array.isArray(data.result)) {
            return data.result as Array<IAttachment>;
          } else {
            return [data.result as IAttachment];
          }
        })
      );
  }

  upload(file: File, reference: string) {
    let formData = new FormData();
    formData.append("fileName", file.name);
    formData.append("reference", reference);
    formData.append("data", file);

    return this.http.post(this._apiUrl, formData)
      .pipe(handleResponse<IAttachment>(this.logger, 'upload attachemnt'));
  }

  delete(attachment: IAttachment) {
    return this.http.delete(`${this._apiUrl}/${attachment.id}`)
      .pipe(handleResponse<number>(this.logger, 'delete attachemnt'));
  }

  download(attachment: IAttachment) {
    const mimeType = environment.mimeTypes.binary;

    return this.http.get(`${this._apiUrl}/${attachment.id}`, {
      headers: { 'Accept': mimeType },
      responseType: 'blob', // This must be a Blob type
    }).subscribe((res: Blob) => {
      this.saveAsBlob(res, attachment.fileName, mimeType);
    });
  }

  private saveAsBlob(data: any, filename: string, mimeType: string) {
    const file = new File([data], filename, { type: mimeType });
    this.fileSaver.save(file, filename);
  }
}

export interface IAttachment {
  id: string,
  fileName: string,
  source: string,
  reference: string,
  viewCount: number,
}